﻿/**
 * DecoratorsBest — Typesense Predictive Search (Modern)
 *
 * Full-featured search panel: trending/popular on focus,
 * live product results on type.  No dependency on the theme's
 * <predictive-search> custom element.
 */

(async function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────────────── */
  var CFG = window.__TYPESENSE_PREDICTIVE_CONFIG || {};
  if (!CFG.apiKey || !CFG.host) return;

  var isTrade     = CFG.isTrade === true;
  var moneyFmt    = CFG.moneyFormat || '${{amount}}';
  var collection  = CFG.collectionName || 'products';
  var tsBase      = 'https://' + CFG.host;
  var tsKey       = CFG.apiKey;

  /* Popular suggestions shown when the input is empty/focused.
     Each entry is { q, url }. When `url` is set the pill is a link to that
     page/collection (e.g. Grasscloth → /collections/grasscloth-wallpaper);
     when it's empty the pill runs `q` as a search (legacy behaviour). The
     live list comes from the admin Search Config (popular_searches), which is
     the source of truth — these mirror it as a fallback. */
  var TRENDING_QUERIES = [
    { q: 'Grasscloth',     url: '/collections/grasscloth-wallpaper' },
    { q: 'Schumacher',     url: '/collections/schumacher' },
    { q: 'York',           url: '/collections/york-wallpaper' },
    { q: 'Kravet',         url: '/pages/kravet-brands' },
    { q: 'Thibaut',        url: '/collections/thibaut-wallpaper' },
    { q: 'Blue Wallpaper', url: '/collections/blue-wallpaper' },
    { q: 'MindTheGap',     url: '/collections/mindthegap' },
    { q: 'Velvet',         url: '/collections/velvet-fabrics' },
    { q: 'Lee Jofa',       url: '/collections/lee-jofa' }
  ];

  /* Known label → URL map from the built-in defaults. Used to re-attach links
     when the backend only sends the legacy trending_queries (plain strings, no
     URLs) — e.g. before the admin API is redeployed with popular_searches —
     so the brand/category pills still link instead of falling back to search. */
  var DEFAULT_TRENDING_URLS = {};
  TRENDING_QUERIES.forEach(function (t) {
    if (t.url) DEFAULT_TRENDING_URLS[t.q.toLowerCase()] = t.url;
  });

  /* Accept either plain strings (legacy trending_queries) or { q/label, url }
     objects (popular_searches) and normalise to { q, url }. */
  function normTrending(list) {
    if (!Array.isArray(list)) return [];
    return list.map(function (t) {
      if (typeof t === 'string') return { q: t, url: '' };
      if (t && typeof t === 'object') return { q: t.q || t.label || '', url: t.url || '' };
      return null;
    }).filter(function (t) { return t && t.q; });
  }

  /* Fallback only — the live list comes from the admin Search Config
     (popular_categories) and is the source of truth. These mirror that config
     so that if the /search-config fetch ever fails the chips still point at
     real pages (the old /collections/* defaults 404'd). */
  var POPULAR_CATEGORIES = [
    { label: 'Wallpaper', url: '/pages/designer-wallpaper' },
    { label: 'Fabric',    url: '/pages/designer-fabrics' },
    { label: 'Trim',      url: '/pages/designer-trim' },
    { label: 'Pillows',   url: '/pages/throw-pillows-by-color' },
    { label: 'Furniture', url: '/pages/furniture' }
  ];


  /* Search parameters — defaults, overridden by search config if reachable.
     MUST stay aligned with config_store.DEFAULTS on the admin backend; these
     are the safety net when the cross-origin /search-config fetch fails. */
  // collection_titles intentionally NOT searched — it matched every generic
  // collection a product sits in ("Beige Wallpaper", "All Kravet Wallpaper"),
  // so "wallpaper" pulled in 68k fabrics. See config_store.DEFAULTS.
  var _qBy      = 'title,sku,mpn,description,vendor,tag_brand,tag_designer,tag_type,tag_color,tag_size,tag_pattern,tag_style,tag_category,tags_raw';
  var _qWeights = '10,8,8,1,4,6,6,5,4,4,4,3,3,1';
  var _numTypos = '2,0,0,0,1,1,1,1,1,1,1,1,1,0';
  /* drop_tokens_threshold=0 forces strict AND across query tokens. Old
     value 2 silently dropped collection-name tokens ("a street springhill"
     → 2,400 vendor catalog results). Per Barbara 5/27. */
  var _dropTokensThreshold = 0;
  var _typoTokensThreshold = 3;
  var _typeFirst = [];   // product types ranked first when a query names one (see typeFirstSortBy)

  /* Load live config from admin dashboard via the admin host (cross-origin).
     The relative-path /search-config does NOT route to the admin server
     through our storefront CDN — a relative fetch silently 404s, the catch
     swallows it, and the hardcoded defaults above are what actually runs.
     Use window.__TYPESENSE_CONFIG.adminHost (set in ts-script.liquid) for
     the cross-origin URL, same as the /search-log POST. */
  try {
    // Prefer the predictive config's adminHost (present on every page); fall
    // back to the search config's (only injected on search/collection pages).
    var _cfgAdminHost = (CFG.adminHost || (window.__TYPESENSE_CONFIG || {}).adminHost || '').replace(/\/+$/, '');
    var _cfgUrl = _cfgAdminHost
      ? 'https://' + _cfgAdminHost + '/search-config'
      : '/search-config';   // legacy fallback — only works if a CDN rule proxies it
    var _cfgResp = await fetch(_cfgUrl, { cache: 'default', mode: 'cors' }).catch(function () { return null; });
    if (_cfgResp && _cfgResp.ok) {
      var _cfgData = await _cfgResp.json().catch(function () { return null; });
      if (_cfgData) {
        if (_cfgData.query_by)               _qBy      = _cfgData.query_by;
        if (_cfgData.query_by_weights)       _qWeights = _cfgData.query_by_weights;
        if (_cfgData.num_typos)              _numTypos = _cfgData.num_typos;
        if (typeof _cfgData.drop_tokens_threshold === 'number') _dropTokensThreshold = _cfgData.drop_tokens_threshold;
        if (typeof _cfgData.typo_tokens_threshold === 'number') _typoTokensThreshold = _cfgData.typo_tokens_threshold;
        if (Array.isArray(_cfgData.type_first)) _typeFirst = _cfgData.type_first;
        /* popular_searches ({label,url} JSON) is authoritative; fall back to
           the legacy trending_queries string list when it isn't set. */
        if (_cfgData.popular_searches) {
          try {
            var _ps = JSON.parse(_cfgData.popular_searches);
            var _psNorm = normTrending(_ps);
            if (_psNorm.length) TRENDING_QUERIES = _psNorm;
          } catch (_) { /* keep defaults */ }
        } else if (Array.isArray(_cfgData.trending_queries) && _cfgData.trending_queries.length) {
          /* Legacy list carries no URLs — backfill from known defaults by label
             so brand/category pills keep linking instead of running a search. */
          TRENDING_QUERIES = normTrending(_cfgData.trending_queries).map(function (t) {
            if (!t.url && DEFAULT_TRENDING_URLS[t.q.toLowerCase()]) {
              t.url = DEFAULT_TRENDING_URLS[t.q.toLowerCase()];
            }
            return t;
          });
        }
        if (_cfgData.popular_categories) {
          try {
            var _cats = JSON.parse(_cfgData.popular_categories);
            if (Array.isArray(_cats) && _cats.length) POPULAR_CATEGORIES = _cats;
          } catch (_) { /* keep defaults */ }
        }
      }
    }
  } catch (_) { /* keep defaults */ }
  /* ── Helpers ────────────────────────────────────────────────── */
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var amt = parseFloat(cents).toFixed(2);
    var commas = amt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFmt
      .replace('{{amount_with_comma_separator}}', amt.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}',
        parseInt(cents, 10).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', parseInt(cents, 10).toString())
      .replace('{{amount}}', commas);
  }

  function esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  function sanitizeHL(s) {
    return s.replace(/<(?!\/?(mark)\b)[^>]*>/gi, '');
  }

  // "New" badge on the predictive card. Same trigger as the PLP: product
  // tag "New" (case-insensitive). Reuses the theme's existing
  // .product-label / .product-label--new CSS so colors match the theme
  // settings. The predictive card is small, so we render the badge inline
  // in the title row rather than absolute-positioned over the thumbnail.
  function renderNewBadge(doc) {
    if (!doc) return '';
    // DecoratorsBest uses Group:New (routes to tag_group field); some
    // products may also have a plain "New" in tags_raw. Check both.
    var pools = [doc.tag_group, doc.tags_raw];
    for (var p = 0; p < pools.length; p++) {
      var arr = pools[p] || [];
      for (var i = 0; i < arr.length; i++) {
        if (String(arr[i] || '').toLowerCase() === 'new') {
          return '<span class="product-label product-label--new tsp-new-badge">New</span>';
        }
      }
    }
    return '';
  }

  /* ── Query-token → filter promotion ───────────────────────────────
     Ported from typesense-search.js so the predictive dropdown returns the
     SAME set as the full results page. When a typed token matches a known
     facet value (brand/type/category/designer) it's promoted to a hard
     filter_by and stripped from the text query — e.g. "grasscloth" becomes
     filter_by tag_category:=Grasscloth (clean wallpaper/fabric) instead of a
     loose full-text match that also pulls furniture/lighting. The dictionary
     is shared with the results page via the same localStorage key, so the two
     implementations stay consistent. */
  var PROMOTABLE_FIELDS = ['tag_brand', 'tag_type', 'tag_category', 'tag_designer'];
  var FACET_DICT_KEY = 'ts_facet_dictionary_v1';
  var FACET_DICT_TTL_MS = 24 * 60 * 60 * 1000;
  var knownFacetValues = {};
  PROMOTABLE_FIELDS.forEach(function (f) { knownFacetValues[f] = new Map(); });

  try {
    var _cachedDict = JSON.parse(localStorage.getItem(FACET_DICT_KEY) || 'null');
    if (_cachedDict && _cachedDict.t && Date.now() - _cachedDict.t < FACET_DICT_TTL_MS && _cachedDict.d) {
      PROMOTABLE_FIELDS.forEach(function (field) {
        var entries = _cachedDict.d[field];
        if (!Array.isArray(entries)) return;
        entries.forEach(function (pair) {
          if (Array.isArray(pair) && pair.length === 2) knownFacetValues[field].set(pair[0], pair[1]);
        });
      });
    }
  } catch (_) { /* localStorage disabled — promotion just no-ops until seeded */ }

  var _persistTimer = null;
  function persistFacetDictionary() {
    if (_persistTimer) return;
    _persistTimer = setTimeout(function () {
      _persistTimer = null;
      try {
        var out = { t: Date.now(), d: {} };
        PROMOTABLE_FIELDS.forEach(function (field) {
          out.d[field] = Array.from(knownFacetValues[field].entries());
        });
        localStorage.setItem(FACET_DICT_KEY, JSON.stringify(out));
      } catch (_) { /* quota exceeded or disabled */ }
    }, 1000);
  }

  // Grow the shared dictionary from a predictive multi_search result's
  // facet_counts (Typesense shape), so predictive self-seeds even if the user
  // never lands on a results/collection page first.
  function ingestPredictiveFacets(result) {
    if (!result || !Array.isArray(result.facet_counts)) return;
    var changed = false;
    result.facet_counts.forEach(function (fc) {
      if (!fc || PROMOTABLE_FIELDS.indexOf(fc.field_name) === -1 || !Array.isArray(fc.counts)) return;
      fc.counts.forEach(function (c) {
        var value = c && c.value;
        if (!value || value.length < 2 || /^\d+$/.test(value)) return;
        var lo = value.toLowerCase();
        if (!knownFacetValues[fc.field_name].has(lo)) {
          knownFacetValues[fc.field_name].set(lo, value);
          changed = true;
        }
      });
    });
    if (changed) persistFacetDictionary();
  }

  function _depluralCandidates(phrase) {
    var spaceIdx = phrase.lastIndexOf(' ');
    var prefix = spaceIdx >= 0 ? phrase.slice(0, spaceIdx + 1) : '';
    var word   = spaceIdx >= 0 ? phrase.slice(spaceIdx + 1)    : phrase;
    var out = [];
    if (/ies$/.test(word))  out.push(prefix + word.replace(/ies$/, 'y'));
    if (/ves$/.test(word)) { out.push(prefix + word.replace(/ves$/, 'f')); out.push(prefix + word.replace(/ves$/, 'fe')); }
    if (/ches$/.test(word)) out.push(prefix + word.replace(/ches$/, 'ch'));
    if (/shes$/.test(word)) out.push(prefix + word.replace(/shes$/, 'sh'));
    if (/xes$/.test(word))  out.push(prefix + word.replace(/xes$/, 'x'));
    if (/ses$/.test(word))  out.push(prefix + word.replace(/ses$/, 's'));
    if (/s$/.test(word) && word.length > 2) out.push(prefix + word.replace(/s$/, ''));
    return out;
  }
  function _lookupFacetValue(field, phrase) {
    var map = knownFacetValues[field];
    if (map.has(phrase)) return map.get(phrase);
    var cands = _depluralCandidates(phrase);
    for (var i = 0; i < cands.length; i++) {
      if (map.has(cands[i])) return map.get(cands[i]);
    }
    return null;
  }
  var MAX_PHRASE_LEN = 4;
  function promoteQueryToFilters(query) {
    if (!query || typeof query !== 'string') return null;
    var tokens = query.trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return null;
    var used = new Array(tokens.length).fill(false);
    var matches = [];
    PROMOTABLE_FIELDS.forEach(function (field) {
      for (var len = Math.min(MAX_PHRASE_LEN, tokens.length); len >= 1; len--) {
        var foundForLen = false;
        for (var start = 0; start + len <= tokens.length; start++) {
          var overlap = false;
          for (var i = start; i < start + len; i++) { if (used[i]) { overlap = true; break; } }
          if (overlap) continue;
          var phrase = tokens.slice(start, start + len).join(' ').toLowerCase();
          var canonical = _lookupFacetValue(field, phrase);
          if (canonical !== null) {
            matches.push({ field: field, value: canonical });
            for (var j = start; j < start + len; j++) used[j] = true;
            foundForLen = true;
            break;
          }
        }
        if (foundForLen) break;
      }
    });
    if (!matches.length) return null;
    var remaining = tokens.filter(function (_, i) { return !used[i]; }).join(' ');
    return { matches: matches, remainingQuery: remaining };
  }
  function buildPromotedFilterClause(matches) {
    return matches.map(function (m) {
      return m.field + ':=`' + String(m.value).replace(/`/g, '') + '`';
    }).join(' && ');
  }

  /* Type-first ranking: when the query names a product type ("dining
     chairs", "tables"), that type ranks first, best sellers first within
     each group. Mirrors typeFirstSortBy() in typesense-search.js so the
     preview leads with the same products as the results page. */
  var typeFirstExact = new Map();   // 'dining chair' -> 'Dining Chair'
  var typeFirstByEnd = new Map();   // 'table' -> ['Accent Table', 'Coffee Table', ...]
  _typeFirst.forEach(function (value) {
    if (typeof value !== 'string' || !value) return;
    var lo = value.toLowerCase();
    typeFirstExact.set(lo, value);
    var end = lo.slice(lo.lastIndexOf(' ') + 1);
    if (end !== lo) typeFirstByEnd.set(end, (typeFirstByEnd.get(end) || []).concat(value));
  });
  function _typeFirstValues(phrase) {
    var cands = [phrase].concat(_depluralCandidates(phrase));
    for (var i = 0; i < cands.length; i++) {
      var exact = typeFirstExact.get(cands[i]) || null;
      var byEnd = typeFirstByEnd.get(cands[i]) || [];
      var values = (exact ? [exact] : []).concat(byEnd.length >= 2 ? byEnd : []);
      if (values.length) return { values: values, exact: exact };
    }
    return null;
  }
  function typeFirstSortBy(query) {
    if (!query || typeof query !== 'string' || !typeFirstExact.size) return null;
    var tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    var used = new Array(tokens.length).fill(false);
    var values = [];
    var titleClauses = [];
    for (var len = Math.min(MAX_PHRASE_LEN, tokens.length); len >= 1; len--) {
      for (var start = 0; start + len <= tokens.length; start++) {
        if (used.slice(start, start + len).some(Boolean)) continue;
        var hit = _typeFirstValues(tokens.slice(start, start + len).join(' '));
        if (!hit) continue;
        hit.values.forEach(function (v) { if (values.indexOf(v) === -1) values.push(v); });
        if (hit.exact && /^[A-Za-z0-9 ]+$/.test(hit.exact)) {
          var words = hit.exact.toLowerCase().split(' ');
          var last = words.pop().replace(/y$/, '');
          titleClauses.push('(' + words.map(function (w) { return 'title:' + w; }).concat('title:' + last + '*').join(' && ') + ')');
        }
        for (var j = start; j < start + len; j++) used[j] = true;
      }
    }
    if (!values.length) return null;
    var list = '[' + values.map(function (v) { return '`' + v.replace(/`/g, '') + '`'; }).join(',') + ']';
    var named = ['tag_type:=' + list, 'tag_subtype:=' + list].concat(titleClauses).join(' || ');
    return '_eval(' + named + '):desc,popularity:desc,_text_match:desc';
  }

  /* ── Typesense helpers ──────────────────────────────────────── */
  // Mirrors the full-search request shape in typesense-search.js so the
  // predictive preview returns the same products in the same order as the
  // full results page (A8 / B1 parity). Differences kept intentionally:
  //   - per_page is small (preview),
  //   - facet_by is limited to the two facets we render (Type, Brand),
  //   - include_fields trims payload to just what the card template uses.
  async function tsSearch(query, perPage) {
    // Promote recognised facet tokens to a hard filter and strip them from the
    // text query, matching the results page (see promotion block above).
    var _filterBy = '';
    var _q = query;
    var _promoted = promoteQueryToFilters(query);
    if (_promoted) {
      _filterBy = buildPromotedFilterClause(_promoted.matches);
      _q = _promoted.remainingQuery || '*';   // '*' = filter-only match-all
    }
    var url = tsBase + '/multi_search?x-typesense-api-key=' + tsKey;
    var body = {
      searches: [{
        collection: collection,
        q: _q,
        filter_by: _filterBy || undefined,   // omitted by JSON.stringify when empty
        query_by: _qBy,
        query_by_weights: _qWeights,
        per_page: perPage || 6,
        highlight_full_fields: 'title',
        num_typos: _numTypos,
        enable_typos_for_alpha_numerical_tokens: false,
        typo_tokens_threshold: _typoTokensThreshold,
        drop_tokens_threshold: _dropTokensThreshold,
        sort_by: typeFirstSortBy(_q) || 'popularity:desc,_text_match:desc',
        // Include the variant tag fields so the predictive card can render
        // the same swatch / "N sizes available" hint as the full results page,
        // strip variant words from the title for grouped cards, expose
        // related_products_json for cross-card colorway swatches, and
        // tags_raw for the "New" badge trigger.
        include_fields: 'title,handle,image_url,vendor,consumer_price,trade_price,consumer_compare_at_price,trade_compare_at_price,b2b_migrated,sku,tag_type,tag_color,tag_size,tag_pattern,tag_group,tags_raw,related_products_json',
        // tag_type,tag_brand drive the displayed facet sections (sliced to 4);
        // tag_category,tag_designer are requested only to self-seed the
        // promotion dictionary (not displayed). max_facet_values bumped so the
        // dictionary fills out, the display code still slices to its top 4.
        facet_by: 'tag_type,tag_brand,tag_category,tag_designer',
        max_facet_values: 20,
        // Collapse color/size siblings of the same design into one card —
        // mirrors typesense-search.js so the predictive count and listing
        // count match. group_limit:5 keeps enough siblings around for the
        // swatch indicator without bloating the response.
        group_by: 'group_id',
        group_limit: 5,
        group_missing_values: true
      }]
    };
    var resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!resp.ok) throw new Error('Typesense search failed');
    var data = await resp.json();
    var result = data.results[0];
    // Grow the shared promotion dictionary from this response's facets.
    try { ingestPredictiveFacets(result); } catch (_) { /* never break search */ }
    return result;
  }

  // ── Variant helpers (duplicated from typesense-search.js — these two
  // files load independently on the storefront and don't share an IIFE).
  function stripVariantTokensFromTitle(title, tokens) {
    if (!title || !tokens || !tokens.length) return title;
    var result = title;
    tokens.forEach(function (token) {
      if (!token) return;
      var escaped = String(token).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp('\\s*' + escaped + '\\s*', 'gi'), ' ');
    });
    return result.replace(/\s+/g, ' ').trim();
  }
  function detectGroupingDimension(siblings) {
    if (!siblings || siblings.length < 2) return { kind: 'none', count: 1 };
    var colors = new Set(), sizes = new Set(), patterns = new Set();
    siblings.forEach(function (s) {
      (s.tag_color   || []).forEach(function (v) { if (v) colors.add(v); });
      (s.tag_size    || []).forEach(function (v) { if (v) sizes.add(v); });
      (s.tag_pattern || []).forEach(function (v) { if (v) patterns.add(v); });
    });
    if (colors.size > 1 && colors.size >= sizes.size && colors.size >= patterns.size) {
      return { kind: 'color', count: colors.size };
    }
    if (sizes.size > 1 && sizes.size >= patterns.size) {
      return { kind: 'size', count: sizes.size };
    }
    if (patterns.size > 1) {
      return { kind: 'pattern', count: patterns.size };
    }
    return { kind: 'generic', count: siblings.length };
  }
  function collectVariantTokens(siblings) {
    var out = new Set();
    siblings.forEach(function (s) {
      (s.tag_color   || []).forEach(function (v) { if (v) out.add(v); });
      (s.tag_size    || []).forEach(function (v) { if (v) out.add(v); });
      (s.tag_pattern || []).forEach(function (v) { if (v) out.add(v); });
    });
    return Array.from(out);
  }
  function renderVariantIndicator(siblings) {
    var dim = detectGroupingDimension(siblings);
    if (dim.kind === 'none') return '';
    // Color swatches now come from related_products_json via
    // renderColorSwatches() — color variants render as separate cards, so the
    // grouped siblings here are size variants of one colorway.
    if (dim.kind === 'color') return '';
    if (dim.kind === 'generic') return '';
    var label = dim.kind === 'size' ? (dim.count + ' sizes available')
              : (dim.count + ' patterns available');
    return '<div class="tsp-variant-hint">' + label + '</div>';
  }

  // Parse the indexed related_products_json (distinct colorways) into an array
  // of {handle, color, image_url}.
  function parseColorSiblings(doc) {
    var raw = doc && doc.related_products_json;
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (e) { return []; }
    }
    return Array.isArray(raw) ? raw : [];
  }

  // Color-swatch row from related_products_json — signals the other colors a
  // design comes in. Renders only when ≥2 colorways have images.
  function renderColorSwatches(doc) {
    var list = parseColorSiblings(doc).filter(function (s) { return s && s.image_url; });
    if (list.length < 2) return '';
    var capped = list.slice(0, 5);
    var swatches = capped.map(function (s) {
      var url = String(s.image_url).replace(/\?.*$/, '') + '?width=48&quality=80';
      return '<span class="tsp-swatch" style="background-image:url(' + url + ')" title="' + esc(s.color || '') + '"></span>';
    }).join('');
    var more = list.length > capped.length
      ? '<span class="tsp-swatch-more">+' + (list.length - capped.length) + '</span>'
      : '';
    return '<div class="tsp-swatches">' + swatches + more + '</div>';
  }

  async function tsTrending() {
    /* Grab a handful of popular products (bestsellers / high-relevance) */
    var url = tsBase + '/multi_search?x-typesense-api-key=' + tsKey;
    var body = {
      searches: [{
        collection: collection,
        q: '*',
        per_page: 4,
        sort_by: 'popularity:desc,published_at:desc',
        include_fields: 'title,handle,image_url,vendor,consumer_price,trade_price,consumer_compare_at_price,trade_compare_at_price,b2b_migrated,tag_type,tag_group,tags_raw',
        filter_by: 'image_url:!=""'
      }]
    };
    var resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!resp.ok) return null;
    var data = await resp.json();
    return data.results[0];
  }

  // isTrade == B2B company membership (the legacy trade tag is retired).
  function price(hit) {
    var d = hit.document;
    if (isTrade) {
      return d.trade_price != null ? d.trade_price : d.consumer_price;
    }
    return d.consumer_price;
  }
  function comparePrice(hit) {
    var d = hit.document;
    // Trade strikes through the consumer price, like the product page and
    // the collection cards; never the consumer site's sale compare-at.
    if (isTrade) {
      return d.consumer_price;
    }
    return d.consumer_compare_at_price;
  }

  /* ── HTML builders ──────────────────────────────────────────── */
  // `siblings` (optional) is the full hits array from a grouped_hits entry
  // when group_by is active — used to clean the title (strip variant words)
  // and render the swatch / "N sizes available" hint. When called without
  // siblings (trending grid), it falls back to a single-hit render.
  function productCard(primary, siblings) {
    var doc = primary.document;
    var hls = primary.highlights || [];
    var title = esc(doc.title);
    var hl = hls.find(function (h) { return h.field === 'title'; });
    if (hl && hl.snippet) title = sanitizeHL(hl.snippet);

    var siblingDocs = (siblings || [primary]).map(function (h) { return h.document; });
    var isGrouped = siblingDocs.length > 1;
    if (isGrouped) {
      var tokens = collectVariantTokens(siblingDocs);
      title = stripVariantTokensFromTitle(title, tokens);
    }
    var indicatorHtml = isGrouped ? renderVariantIndicator(siblingDocs) : '';
    // Color swatches come from related_products_json (independent of grouping).
    var colorSwatchHtml = renderColorSwatches(doc);

    var p = price(primary);
    var cp = comparePrice(primary);
    var img = doc.image_url
      ? doc.image_url.replace(/\?.*$/, '') + '?width=120&quality=80'
      : '';
    var type = doc.tag_type && doc.tag_type.length ? doc.tag_type[0] : '';
    var onSale = cp && cp > p;
    var priceHTML = p != null
      ? (onSale
          ? '<span class="tsp-card__compare">' + formatMoney(cp) + '</span> <span class="tsp-card__sale">' + formatMoney(p) + '</span>'
          : '<span>' + formatMoney(p) + '</span>')
      : '';

    return '<a href="/products/' + doc.handle + '" class="tsp-card">'
      + '<div class="tsp-card__img-wrap">'
      + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '<div class="tsp-card__no-img"><svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>')
      + '</div>'
      + '<div class="tsp-card__body">'
      + '<div class="tsp-card__title">' + renderNewBadge(doc) + title + '</div>'
      + '<div class="tsp-card__vendor">' + esc(doc.vendor) + (type ? ' &middot; ' + esc(type) : '') + '</div>'
      + colorSwatchHtml
      + indicatorHtml
      + '<div class="tsp-card__price">' + priceHTML + '</div>'
      + '</div>'
      + '</a>';
  }

  function buildDefaultPanel(trendingProducts) {
    var html = '<div class="tsp-panel tsp-panel--default">';

    /* Popular categories */
    html += '<div class="tsp-section">'
      + '<div class="tsp-section__heading">Shop by category</div>'
      + '<div class="tsp-pills">';
    POPULAR_CATEGORIES.forEach(function (c) {
      html += '<a href="' + c.url + '" class="tsp-pill tsp-pill--cat">' + esc(c.label) + '</a>';
    });
    html += '</div></div>';

    /* Trending searches */
    html += '<div class="tsp-section">'
      + '<div class="tsp-section__heading">Popular searches</div>'
      + '<div class="tsp-pills">';
    TRENDING_QUERIES.forEach(function (t) {
      var icon = '<svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>';
      if (t.url) {
        html += '<a href="' + esc(t.url) + '" class="tsp-pill">' + icon + esc(t.q) + '</a>';
      } else {
        html += '<button type="button" class="tsp-pill" data-query="' + esc(t.q) + '">'
          + icon + esc(t.q) + '</button>';
      }
    });
    html += '</div></div>';

    /* Trending products */
    if (trendingProducts && trendingProducts.hits && trendingProducts.hits.length) {
      html += '<div class="tsp-section">'
        + '<div class="tsp-section__heading">Trending products</div>'
        + '<div class="tsp-products tsp-products--grid">';
      trendingProducts.hits.forEach(function (hit) {
        html += productCard(hit);
      });
      html += '</div></div>';
    }

    html += '</div>';
    return html;
  }

  function buildResultsPanel(result, query) {
    // With group_by:'group_id' on the request, Typesense returns
    // `grouped_hits` instead of (or alongside) `hits`. Each entry is one
    // unique design; we render its representative hit (hits[0]) as the
    // card and pass the rest as siblings so the title/swatch logic can
    // collapse color or size variants. result.found is the GROUP count
    // when group_by is active, which is what we want as the headline.
    var groupedHits = result.grouped_hits;
    var entries;
    if (groupedHits && groupedHits.length) {
      entries = groupedHits.map(function (gh) {
        return { primary: gh.hits[0], siblings: gh.hits };
      });
    } else {
      entries = (result.hits || []).map(function (h) { return { primary: h, siblings: [h] }; });
    }
    var hits = entries.map(function (e) { return e.primary; });   // for downstream length checks
    var total = result.found || 0;
    var facets = result.facet_counts || [];

    if (hits.length === 0) {
      return '<div class="tsp-panel tsp-panel--empty">'
        + '<div class="tsp-empty">'
        + '<svg width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>'
        + '<p>No results for &ldquo;<strong>' + esc(query) + '</strong>&rdquo;</p>'
        + '<span>Try checking your spelling or using more general terms</span>'
        + '</div></div>';
    }

    var html = '<div class="tsp-panel tsp-panel--results">';

    /* Left: suggestions column */
    html += '<div class="tsp-suggestions">';

    /* Facet: Product types */
    var typeFacet = facets.find(function (f) { return f.field_name === 'tag_type'; });
    if (typeFacet && typeFacet.counts && typeFacet.counts.length) {
      html += '<div class="tsp-section">'
        + '<div class="tsp-section__heading">Product types</div>'
        + '<ul class="tsp-facet-list">';
      typeFacet.counts.slice(0, 4).forEach(function (c) {
        // Refine the current search by type — keeps the query context (e.g.
        // "grasscloth" + Wallpaper). This is a search refinement, NOT category
        // navigation; the "Shop by category" chips handle URL-page navigation.
        html += '<li><a href="/search?q=' + encodeURIComponent(query) + '&tag_type=' + encodeURIComponent(c.value) + '" class="tsp-facet-link">'
          + esc(c.value)
          + '<span class="tsp-facet-count">' + c.count.toLocaleString() + '</span>'
          + '</a></li>';
      });
      html += '</ul></div>';
    }

    /* Facet: Brands */
    var brandFacet = facets.find(function (f) { return f.field_name === 'tag_brand'; });
    if (brandFacet && brandFacet.counts && brandFacet.counts.length) {
      html += '<div class="tsp-section">'
        + '<div class="tsp-section__heading">Brands</div>'
        + '<ul class="tsp-facet-list">';
      brandFacet.counts.slice(0, 4).forEach(function (c) {
        // Force popularity sort so a brand's best-sellers lead (e.g. Thibaut
        // Katsura) instead of relevance order surfacing trim first. The sort
        // value is the InstantSearch index name the /search page expects.
        var brandSort = encodeURIComponent(collection + '/sort/popularity:desc');
        html += '<li><a href="/search?q=' + encodeURIComponent(query) + '&tag_brand=' + encodeURIComponent(c.value) + '&sort=' + brandSort + '" class="tsp-facet-link">'
          + esc(c.value)
          + '<span class="tsp-facet-count">' + c.count.toLocaleString() + '</span>'
          + '</a></li>';
      });
      html += '</ul></div>';
    }

    html += '</div>';

    /* Right: product results */
    html += '<div class="tsp-results-main">';
    html += '<div class="tsp-section__heading">Products <span class="tsp-results-count">' + total.toLocaleString() + ' results</span></div>';
    html += '<div class="tsp-products tsp-products--list">';
    entries.forEach(function (e) {
      html += productCard(e.primary, e.siblings);
    });
    html += '</div>';

    if (total > hits.length) {
      html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="tsp-view-all">'
        + 'View all ' + total.toLocaleString() + ' results'
        + '<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>'
        + '</a>';
    }
    html += '</div>';
    html += '</div>';

    return html;
  }

  /* ── Controller ─────────────────────────────────────────────── */
  function init() {
    var input = document.getElementById('header-search')
      || document.querySelector('.header .js-search-input')
      || document.querySelector('.header .search__input');
    if (!input) return;

    input.setAttribute('autocomplete', 'off');

    /* Build panel container */
    var panel = document.createElement('div');
    panel.className = 'tsp-container';
    panel.setAttribute('id', 'tsp-container');
    panel.style.display = 'none';

    /* Find the right parent — the header__search wrapper */
    var headerSearch = input.closest('.header__search') || input.closest('form').parentNode;
    headerSearch.style.position = 'relative';
    headerSearch.appendChild(panel);

    /* Track open timestamp to prevent focus→click race */
    var openedAt = 0;

    var debounceTimer = null;
    var currentQuery = '';
    var isOpen = false;
    var selectedIndex = -1;
    var cachedDefault = null;
    var fetchingDefault = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      openedAt = Date.now();
      panel.style.display = '';
      input.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('tsp-open');
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      panel.style.display = 'none';
      input.setAttribute('aria-expanded', 'false');
      selectedIndex = -1;
      document.documentElement.classList.remove('tsp-open');
    }

    /* Default panel (trending / popular) */
    async function showDefault() {
      if (cachedDefault) {
        panel.innerHTML = cachedDefault;
        bindPillClicks();
        open();
        return;
      }
      if (fetchingDefault) { open(); return; }
      fetchingDefault = true;

      /* Show skeleton immediately */
      panel.innerHTML = '<div class="tsp-panel tsp-panel--default">'
        + '<div class="tsp-section"><div class="tsp-section__heading">Popular searches</div>'
        + '<div class="tsp-pills">' + TRENDING_QUERIES.map(function () { return '<span class="tsp-pill tsp-pill--skel"></span>'; }).join('') + '</div></div>'
        + '<div class="tsp-section"><div class="tsp-section__heading">Trending products</div>'
        + '<div class="tsp-products tsp-products--grid">'
        + '<div class="tsp-card tsp-card--skel"></div><div class="tsp-card tsp-card--skel"></div><div class="tsp-card tsp-card--skel"></div><div class="tsp-card tsp-card--skel"></div>'
        + '</div></div></div>';
      open();

      try {
        var trending = await tsTrending();
        cachedDefault = buildDefaultPanel(trending);
        /* Only show if still in default state */
        if (isOpen && !input.value.trim()) {
          panel.innerHTML = cachedDefault;
          bindPillClicks();
        }
      } catch (err) {
        console.error('[TS Predictive] Trending error:', err);
        cachedDefault = buildDefaultPanel(null);
        if (isOpen && !input.value.trim()) {
          panel.innerHTML = cachedDefault;
          bindPillClicks();
        }
      }
      fetchingDefault = false;
    }

    function bindPillClicks() {
      panel.querySelectorAll('.tsp-pill[data-query]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var q = btn.getAttribute('data-query');
          input.value = q;
          input.focus();
          doSearch(q);
        });
      });
    }

    async function doSearch(query) {
      currentQuery = query;
      /* Show a mini loading state */
      panel.classList.add('tsp-loading');
      try {
        var result = await tsSearch(query, 6);
        if (query !== currentQuery) return;
        panel.innerHTML = buildResultsPanel(result, query);
        panel.classList.remove('tsp-loading');
        selectedIndex = -1;
        open();
        /* Analytics — log autocomplete searches with source='predictive'.
           Posts cross-origin to settings.ts_admin_host; storefront CDN
           doesn't proxy POSTs. Lossy by design. */
        try {
          var adminHost = ((window.__TYPESENSE_CONFIG || {}).adminHost || '').replace(/\/+$/, '');
          if (adminHost) {
            var nbHits = (result && result.found) || 0;
            fetch('https://' + adminHost + '/search-log', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ q: query, result_count: nbHits, source: 'predictive' }),
              keepalive: true,
              mode: 'cors',
            }).catch(function () {});
          }
        } catch (_) {}
      } catch (err) {
        console.error('[TS Predictive] Search error:', err);
        panel.classList.remove('tsp-loading');
      }
    }

    /* ── Event listeners ──────────────────────────────────────── */
    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= 2) {
        if (panel.innerHTML) open();
        else doSearch(q);
      } else {
        showDefault();
      }
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceTimer);

      if (q.length < 2) {
        showDefault();
        return;
      }

      debounceTimer = setTimeout(function () {
        doSearch(q);
      }, 200);
    });

    input.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      var items = panel.querySelectorAll('.tsp-card');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = (selectedIndex + 1) % items.length;
        updateSel(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
        updateSel(items);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        if (items[selectedIndex] && items[selectedIndex].href) {
          window.location.href = items[selectedIndex].href;
        }
      } else if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });

    function updateSel(items) {
      items.forEach(function (el, i) {
        el.classList.toggle('tsp-card--active', i === selectedIndex);
      });
      if (selectedIndex >= 0 && items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }

    document.addEventListener('click', function (e) {
      /* Ignore clicks within 150ms of opening (focus→click race) */
      if (Date.now() - openedAt < 150) return;
      var form = input.closest('form');
      var inSearchArea = panel.contains(e.target)
        || e.target === input
        || (form && form.contains(e.target));
      if (!inSearchArea) {
        close();
      }
    });

    /* Close when navigating away (SPA-friendly) */
    window.addEventListener('pageshow', close);
  }

  /* ── Boot ────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
